/**
 * ログインフォームの入力保持（ログイン画面 SCR-01 / 試作サイト LoginForm 準拠）
 *
 * 試作サイト（React SPA）はログイン失敗後も入力欄の値が残る。本アプリはサーバー描画で
 * 「POST → リダイレクト → GET」するため値が失われる。そこで送信直前に sessionStorage へ
 * 退避し、失敗して戻ってきたら復元する。パスワードをサーバー応答 HTML に埋め込まないための方式で、
 * 退避データは復元後およびログイン成功後（/ への遷移時）に必ず破棄する。
 *
 * マークアップ規約:
 *   <form data-lms-login-persist>
 *     <input name="username"> <input name="password"> ...
 *   </form>
 */
const KEY = "lms.login.draft";

interface LoginDraft {
  username?: string;
  password?: string;
}

function clearDraft(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* プライベートモード等で sessionStorage 不可でも無視 */
  }
}

export function initLoginFormPersist(scope: ParentNode = document): void {
  // ログイン画面以外の読み込み（＝ログイン成功で / に遷移した直後など）では退避データを残さない
  if (window.location.pathname !== "/login") {
    clearDraft();
    return;
  }

  const form = scope.querySelector<HTMLFormElement>("form[data-lms-login-persist]");
  if (!form || form.dataset["lmsLoginPersistReady"] === "1") return;
  form.dataset["lmsLoginPersistReady"] = "1";

  const email = form.querySelector<HTMLInputElement>('input[name="username"]');
  const password = form.querySelector<HTMLInputElement>('input[name="password"]');
  if (!email || !password) return;

  // エラーで戻ってきたときだけ復元する（成功時は / に遷移するのでここには来ない）
  const hasError = document.querySelector(".lms-login-error, .lms-field-error") !== null;
  if (hasError) {
    try {
      const raw = window.sessionStorage.getItem(KEY);
      if (raw) {
        const draft = JSON.parse(raw) as LoginDraft;
        // メールはサーバーが formEmail で埋めている場合があるので、空のときだけ復元する
        if (email.value === "" && typeof draft.username === "string") email.value = draft.username;
        if (typeof draft.password === "string") password.value = draft.password;
        // 目アイコン表示・送信ボタン活性を再判定させる（各部品の input リスナーを起こす）
        email.dispatchEvent(new Event("input", { bubbles: true }));
        password.dispatchEvent(new Event("input", { bubbles: true }));
      }
    } catch {
      /* 壊れた退避データは無視する */
    }
  }
  // 復元済み・不要になった退避データは消す
  clearDraft();

  // 送信直前に退避する（次の GET /login で復元できるように）
  form.addEventListener("submit", () => {
    try {
      window.sessionStorage.setItem(
        KEY,
        JSON.stringify({ username: email.value, password: password.value }),
      );
    } catch {
      /* 保存できなくても送信は続行する */
    }
  });
}
